import { Dinero } from "dinero.js";
import React, { type JSX } from "react";
import { Image } from "tsi-common-react/src/components/Image/index";
import { Link } from "tsi-common-react/src/components/Link/index";
import { RawHTML } from "tsi-common-react/src/components/RawHTML/index";
import { AddToCartType } from "tsi-common-react/src/constants";
import { IProduct } from "tsi-common-react/src/models/catalogue";
import { isoMonths } from "tsi-common-react/src/models/nominals";
import { trackAddToBasketEvent } from "tsi-common-react/src/utils/analytics";
import { listFinancingPlans } from "tsi-common-react/src/utils/financing";
import { format } from "tsi-common-react/src/utils/format";
import { getDinero, getMonthlyPrice } from "tsi-common-react/src/utils/money";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { IBasketLineSuggestionProps } from "./BasketLineSuggestion.interfaces";

import styles from "./index.module.scss";

function scrollToFinePrint(event: React.MouseEvent<HTMLElement>): void {
    event.preventDefault();

    const document = getDocument();
    const finePrint = document?.querySelector("#basket-lines__fine-print");
    finePrint?.scrollIntoView({ behavior: "smooth" });
}

function buildImage(product: IProduct): JSX.Element | null {
    // Use the product image if one exists else try the parent image
    let image = product.images.slice().shift();
    if (!image && product.parent) {
        image = product.parent.images.slice().shift();
    }
    // TODO: Need link title since content is an image. See #13290
    return image ? (
        <Link href={product.parent?.link || product.link}>
            <Image
                alt={product.title}
                src={image.original}
                className="basket-line__graphic__image"
            />
        </Link>
    ) : null;
}

function buildOptions(product: IProduct): JSX.Element | null {
    const attributes = product.attributes;
    const optionKeys = attributes.product_options?.value || [];
    if (optionKeys.length <= 0) {
        return null;
    }

    const options = optionKeys.map((key) => {
        const attr = attributes[key];
        if (!attr) {
            return null;
        }
        return (
            <li key={key} className="basket-suggestion__details__option">
                <span>{attr.name}</span>: <strong>{attr.value}</strong>
            </li>
        );
    });

    return (
        <div className={styles.basketSuggestionDetailsOptions}>
            <ul>{options}</ul>
        </div>
    );
}

function calcPlanMonths(total: Dinero): number | null {
    const plans = listFinancingPlans();
    const availablePlans = plans.filter((plan) => {
        return plan.threshold.lessThanOrEqual(total);
    });
    if (availablePlans.length === 0) {
        return null;
    }
    const bestPlan = availablePlans[availablePlans.length - 1];
    return isoMonths.unwrap(bestPlan.length);
}

function buildFinancing(
    product: IProduct,
    basketTotal: Dinero,
): JSX.Element | null {
    if (!product.price.enable_per_month_pricing) {
        return null;
    }

    // Calculate Suggested plan based on (Cart total + Suggestion Product total) / (Highest plan that total qualifies for)
    const basketTotalPrice = getDinero(basketTotal);
    const extraProductPrice = getDinero(
        product.price.cosmetic_excl_tax || "0.00",
    );
    const computedTotalPrice = basketTotalPrice.add(extraProductPrice);
    const planMonths = calcPlanMonths(computedTotalPrice);
    if (!planMonths) {
        return null;
    }

    const computedMonthlyPrice = getMonthlyPrice(
        getDinero(extraProductPrice),
        planMonths,
    );
    // TODO(i18n): Use jt to keep complete sentence together with interpolated components: jt`or ${priceLink} for ${monthsLink}` where priceLink and monthsLink contain the underlined spans and superscripts
    return (
        <p>
            or{" "}
            <a onClick={scrollToFinePrint}>
                <span className={styles.basketSuggestionFinancingUnderline}>
                    {format.money(computedMonthlyPrice)}/mo.
                </span>
                <sup>2</sup> for{" "}
                <span className={styles.basketSuggestionFinancingUnderline}>
                    {planMonths} months
                </span>
                <sup>1</sup>
            </a>
        </p>
    );
}

function getHeadline(props: IBasketLineSuggestionProps): string {
    const bundles = props.line.bundles;
    // TODO(i18n): Wrap in t tagged template: t`FORGETTING SOMETHING?`
    let headline = "FORGETTING SOMETHING?";

    if (bundles.length > 0) {
        // change the default to 'FORGETTING SOMETHING?'
        // TODO(i18n): Wrap comparison string and fallback in t tagged templates
        headline =
            bundles[0].bundle_group.headline !== "Forget Something?"
                ? bundles[0].bundle_group.headline
                : "FORGETTING SOMETHING?";
    }

    return headline;
}

export const BasketLineSuggestion = (
    props: IBasketLineSuggestionProps,
): React.ReactNode => {
    const handleAddToBasket = async () => {
        await props.addBasketLine(props.product.url, 1);
        trackAddToBasketEvent(
            props.product,
            1,
            AddToCartType.IN_CART_UP_CROSS_SELL,
        );
    };

    const line = props.line;
    const lineProduct = line.product;
    const lineRootProduct = lineProduct.parent || lineProduct;

    const product = props.product;
    const rootProduct = product.parent || product;

    const financing = buildFinancing(product, props.basketTotal);
    const headline = getHeadline(props);

    return (
        <div className={styles.basketSuggestion}>
            <div
                className={concatClassNames([
                    styles.basketSuggestionRow,
                    styles.basketSuggestionRowShaded,
                ])}
            >
                <h4>{headline}</h4>
            </div>
            <div className={styles.basketSuggestionRow}>
                <div className={styles.basketSuggestionGraphic}>
                    {buildImage(product)}
                </div>
                <div className={styles.basketSuggestionDetails}>
                    {/* TODO(i18n): Use t with interpolation for complete sentence: {t`BEST SELLER for ${lineRootProduct.title}`} */}
                    <h5>BEST SELLER for {lineRootProduct.title}</h5>
                    <h4 className={styles.basketSuggestionDetailsTitle}>
                        <Link href={rootProduct.link}>{rootProduct.title}</Link>
                    </h4>
                    <RawHTML
                        className={styles.basketSuggestionDetailsDescription}
                        html={rootProduct.description}
                    />
                    {buildOptions(product)}
                </div>
                <div
                    className={concatClassNames([
                        styles.basketSuggestionPrice,
                        "basket-suggestion__price",
                    ])}
                >
                    <p>{format.money(product.price.cosmetic_excl_tax)}</p>
                    <div className={styles.basketSuggestionFinancing}>
                        {financing}
                    </div>
                    {/* TODO(i18n): Wrap button text in t tagged template: {t`Add to cart`} */}
                    <button
                        className="button al-basket-addtocart-upsell"
                        onClick={handleAddToBasket}
                    >
                        Add to cart
                    </button>
                </div>
            </div>
        </div>
    );
};
